import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar'
import { Switch } from '@/shared/components/ui/switch'
import type { Settings } from '@/shared/cache/slices/settings.types'
import { useAuthCache } from '@/shared/cache'
import {
  useActivityOptions,
  useTaskOptions,
} from '@/v2/components/entry-form/useCodeOptions'
import { SettingGroup, SettingRow } from '../SettingsShared'

/** Sentinel for the clearable option — Radix Select can't hold an empty value. */
const NO_DEFAULT = '__none__'

export interface BillingSettingsProps {
  settings: Settings
  update: (patch: Partial<Settings>) => void
}

export function BillingSettings({ settings, update }: BillingSettingsProps) {
  const { auth } = useAuthCache()
  // No matterId: the firm's default-group codes, which is exactly the set the
  // settings endpoint validates a default against.
  const {
    tasks,
    isLoading: tasksLoading,
    isError: tasksError,
  } = useTaskOptions('')
  const {
    activities,
    isLoading: activitiesLoading,
    isError: activitiesError,
  } = useActivityOptions('')
  // A failed fetch also yields an empty list, which would leave "None" as the
  // only choice — picking it would clear a default the user still has.
  const tasksUnavailable = tasksLoading || tasksError
  const activitiesUnavailable = activitiesLoading || activitiesError

  const hasNoDefaults =
    !settings.p_default_task_code && !settings.p_default_activity_code

  return (
    <>
      <SettingGroup label="Profile">
        <div className="flex gap-4 py-5 border-b border-border/40">
          <div className="w-1/3 flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">Name</label>
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-xs font-medium">
                  {auth.initials}
                </AvatarFallback>
              </Avatar>
              <Input
                value={auth.displayName}
                disabled
                className="w-full opacity-70 cursor-not-allowed"
              />
            </div>
          </div>
          <div className="w-2/3 flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">Email</label>
            <Input
              value={auth.email}
              disabled
              className="w-full opacity-70 cursor-not-allowed"
            />
          </div>
        </div>
        <SettingRow
          label="Practice area"
          description="Your area of legal practice, used to tailor generated narratives"
        >
          <Input
            value={settings.practice_type}
            onChange={(e) => update({ practice_type: e.target.value })}
            className="w-52"
          />
        </SettingRow>
        <SettingRow
          label="Role"
          description="Your professional role (e.g. Associate, Partner)"
        >
          <Input
            value={settings.prof_type}
            onChange={(e) => update({ prof_type: e.target.value })}
            className="w-52"
          />
        </SettingRow>
      </SettingGroup>

      <SettingGroup label="Billing">
        <SettingRow
          label="Minimum billable increments"
          description="Smallest time increment used when rounding billable hours"
        >
          <Select
            value={settings.minimum_units ? '0.2' : '0.1'}
            onValueChange={(v) => update({ minimum_units: v === '0.2' })}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0.1">0.1 hours</SelectItem>
              <SelectItem value="0.2">0.2 hours</SelectItem>
            </SelectContent>
          </Select>
        </SettingRow>

        <SettingRow
          label="Default task code"
          description="Pre-fills new time entries when the matter allows it."
        >
          <Select
            value={settings.p_default_task_code ?? NO_DEFAULT}
            onValueChange={(v) => {
              const next = v === NO_DEFAULT ? null : v
              update({
                p_default_task_code: next,
                // Clearing the last default hides the toggle below, so turn it
                // off in the same save rather than stranding it on with nothing
                // to apply — and unreachable.
                ...(!next && !settings.p_default_activity_code
                  ? { p_hardcode_codes: false }
                  : {}),
              })
            }}
            disabled={tasksUnavailable}
          >
            <SelectTrigger className="w-72">
              {/* Radix renders SelectValue's placeholder only for an empty
                  value, and ours is never empty (NO_DEFAULT at minimum), so
                  these states have to render in the trigger itself. */}
              {tasksError ? (
                <span className="text-muted-foreground">
                  Couldn't load task codes
                </span>
              ) : tasksLoading ? (
                <span className="text-muted-foreground">Loading…</span>
              ) : (
                <SelectValue />
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_DEFAULT}>None</SelectItem>
              {tasks.map((t) => (
                <SelectItem key={t.taskCode} value={t.taskCode}>
                  {t.task} — {t.taskDescription}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingRow>

        <SettingRow
          label="Default activity code"
          description="Pre-fills new time entries when the matter allows it."
        >
          <Select
            value={settings.p_default_activity_code ?? NO_DEFAULT}
            onValueChange={(v) => {
              const next = v === NO_DEFAULT ? null : v
              update({
                p_default_activity_code: next,
                ...(!next && !settings.p_default_task_code
                  ? { p_hardcode_codes: false }
                  : {}),
              })
            }}
            disabled={activitiesUnavailable}
          >
            <SelectTrigger className="w-72">
              {activitiesError ? (
                <span className="text-muted-foreground">
                  Couldn't load activity codes
                </span>
              ) : activitiesLoading ? (
                <span className="text-muted-foreground">Loading…</span>
              ) : (
                <SelectValue />
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_DEFAULT}>None</SelectItem>
              {activities.map((a) => (
                <SelectItem key={a.activityCode} value={a.activityCode}>
                  {a.activity} — {a.activityDescription}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingRow>

        {/* Hidden rather than disabled when there is no default: a disabled
            switch can't be turned back off. Still shown while the flag is on, so
            an account already left in that state has a way to clear it. */}
        {(!hasNoDefaults || settings.p_hardcode_codes) && (
          <SettingRow
            label="Use these codes instead of predicting"
            description="Automatically tracked time gets these codes rather than predicted ones. Manual entries pre-fill either way."
          >
            <Switch
              checked={settings.p_hardcode_codes}
              onCheckedChange={(v) => update({ p_hardcode_codes: v })}
            />
          </SettingRow>
        )}
      </SettingGroup>

    </>
  )
}
